import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

const CustomAudioPlayer = ({ previews, dispatch, hideWhenEmpty = false }) => {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const sliderRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Always get the last preview, even if array is empty
  const lastPreview = previews && previews.length > 0 ? previews[previews.length - 1] : null;

  // Listen for play preview requests from the page
  useEffect(() => {
    const handlePlayRequest = () => {
      if (audioRef.current && lastPreview) {
        audioRef.current.play().catch(e => console.log('Play failed:', e));
      }
    };

    window.addEventListener('play-preview-requested', handlePlayRequest);
    return () => {
      window.removeEventListener('play-preview-requested', handlePlayRequest);
    };
  }, [lastPreview]);

  useEffect(() => {
    // Only run effect if we have a preview and audio element
    if (!lastPreview || !lastPreview.preview || !audioRef.current) {
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        const progressPercent = (audio.currentTime / audio.duration) * 100;
        setProgress(progressPercent);

        // Update time display
        const minutes = Math.floor(audio.currentTime / 60);
        const seconds = Math.floor(audio.currentTime % 60);
        setCurrentTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    };

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => {
      setIsLoading(false);
      // Auto-play when audio is ready since user clicked to play
      audio.play().catch(e => console.log('Auto-play failed:', e));
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime("0:00");
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    // Load audio and prepare for auto-play
    audio.src = lastPreview.preview;
    audio.load();

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [lastPreview?.preview]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(e => console.log('Play failed:', e));
    }
  };

  const handleSliderClick = (e) => {
    const audio = audioRef.current;
    const slider = sliderRef.current;
    if (!audio || !slider || !audio.duration) return;

    const rect = slider.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * audio.duration;

    audio.currentTime = newTime;
  };

  const closePlayer = () => {
    // Dispatch action to clear previews
    dispatch({ type: 'ALL_PREVIEWS', previews: [] });
  };

  // Always show player, but with placeholder content when no audio
  const hasPreview = previews && previews.length > 0 && lastPreview;

  // If hideWhenEmpty is true and no previews, don't render anything
  if (hideWhenEmpty && !hasPreview) {
    return null;
  }

  return (
    <div className="fixed-bottom-audio-player">
      <div className="audio-player-content">
        <img
          className="preview-image"
          src={hasPreview ? lastPreview.image : ''}
          alt={hasPreview ? lastPreview.name : 'No audio selected'}
        />

        <div className="preview-info">
          <h4 data-placeholder={!hasPreview}>{hasPreview ? lastPreview.name : 'No audio selected'}</h4>
          <p data-placeholder={!hasPreview}>{hasPreview ? lastPreview.manufacturer_name : 'Select audio to preview'}</p>
        </div>

        <div className="audio-controls">
          <div className={`audio xfer-audio-player ${isLoading ? 'loading' : ''} ${!hasPreview ? 'disabled' : ''}`}>
            <div className="loading">
              <div className="spinner"></div>
            </div>

            <div className="play-pause-btn" onClick={hasPreview ? togglePlayPause : undefined}>
              <i className={`fa ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
            </div>

            <div className="controls">
              <span className="current-time">{currentTime}</span>
              <div
                className="slider"
                ref={sliderRef}
                onClick={hasPreview ? handleSliderClick : undefined}
              >
                <div className="progress" style={{ width: `${progress}%` }}>
                  <div className="pin"></div>
                </div>
              </div>
            </div>

            {hasPreview && (
              <audio ref={audioRef}>
                <source src={lastPreview.preview} type="audio/mpeg" />
              </audio>
            )}
          </div>
        </div>

        {hasPreview && (
          <button className="close-btn" onClick={closePlayer}>
            <i className="fa fa-times"></i>
          </button>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  previews: state.previews
});

export default connect(mapStateToProps)(CustomAudioPlayer);